import type { SeoReportVisualSignalsContent } from '@dupe/common/research-report'
import {
  hasVisualSignalsChartPayload,
  hasVisualSignalsRadarPayload,
  hasVisualSignalsScatterPayload,
} from '@dupe/common/research-report/visual-signals'
import { cn } from '~/src/lib/utils'
import { SeoReportRadarProductComparisonSection } from './SeoReportRadarProductComparisonSection'
import { SeoReportPricePerformanceScatterSection } from './SeoReportPricePerformanceScatterSection'

interface SeoReportVisualSignalsProps {
  visualSignals: SeoReportVisualSignalsContent
  className?: string
}

function formatScatterMetricValue(
  value: number,
  unit: string | null | undefined,
  compact: boolean
): string {
  const numberText = Number.isInteger(value) ? String(value) : value.toFixed(1)
  const normalizedUnit = unit?.trim()
  if (!normalizedUnit) return numberText
  if (compact && /^(hours?|hrs?)$/i.test(normalizedUnit)) {
    return `${numberText}h`
  }
  return `${numberText} ${normalizedUnit}`
}

function buildScatterAxisLabel(
  label: string,
  unit: string | null | undefined
): string {
  const normalizedUnit = unit?.trim()
  const base = label.toUpperCase()
  return normalizedUnit
    ? `${base} (${normalizedUnit.toUpperCase()}) →`
    : `${base} →`
}

export function SeoReportVisualSignals({
  visualSignals,
  className,
}: SeoReportVisualSignalsProps) {
  if (!hasVisualSignalsChartPayload(visualSignals)) return null

  const showRadar = hasVisualSignalsRadarPayload(visualSignals)
  const showScatter = hasVisualSignalsScatterPayload(visualSignals)
  const radar = visualSignals.dimensionComparison
  const scatter = visualSignals.pricePerformance
  const sectionTitle = visualSignals.sectionTitle ?? 'Visual signals'

  // The umbrella section header only earns its place when it groups more than
  // one chart. With a single chart that chart's own heading is sufficient, so
  // showing the umbrella too leaves a stranded "empty" heading above it.
  const showSectionHeader = showRadar && showScatter

  return (
    <section
      id='visual-signals'
      className={cn('scroll-mt-14 space-y-8', className)}
    >
      {showSectionHeader ? (
        <div className='max-w-[768px] text-left'>
          <h2 className='font-display text-2xl font-bold leading-8 text-foreground'>
            {sectionTitle}
          </h2>
          {visualSignals.sectionDescription ? (
            <p className='mt-3 text-base leading-6 text-foreground-secondary'>
              {visualSignals.sectionDescription}
            </p>
          ) : null}
        </div>
      ) : null}

      {showRadar ? (
        <SeoReportRadarProductComparisonSection
          title={radar.title ?? `How the top ${radar.products.length} compare`}
          description={
            radar.description ??
            'Relative scores across the dimensions that mattered most for this search.'
          }
          summary={radar.summary}
          metrics={radar.dimensions.map((dimension) => ({
            key: dimension.key,
            label: dimension.label,
          }))}
          series={radar.products.map((product) => ({
            id: product.slug,
            name: product.label,
            values: Object.fromEntries(
              radar.dimensions.map((dimension) => [
                dimension.key,
                radar.matrix[product.slug]?.[dimension.key]?.score ?? 0,
              ])
            ),
          }))}
          headingVariant='report'
          className='py-0'
        />
      ) : null}

      {showScatter && scatter ? (
        <SeoReportPricePerformanceScatterSection
          title={scatter.title ?? `Price vs. ${scatter.metricLabel}`}
          description={
            scatter.description ??
            `See how the top picks trade off price against ${scatter.metricLabel.toLowerCase()}.`
          }
          summary={scatter.summary}
          points={scatter.points.map((point) => ({
            id: point.slug,
            name: point.label,
            price: point.price,
            metricValue: point.metricValue,
            tooltipLabel: point.label,
            pointLabel:
              point.pointLabel ?? (point.rank === 1 ? 'TOP\nPICK' : undefined),
            isTopPick: point.rank === 1,
            isHighlighted: point.rank === 1,
          }))}
          yAxisLabel={buildScatterAxisLabel(
            scatter.metricLabel,
            scatter.metricUnit
          )}
          formatMetricTick={(value) =>
            typeof value === 'number'
              ? formatScatterMetricValue(value, scatter.metricUnit, true)
              : String(value)
          }
          formatMetricDetail={(value) =>
            formatScatterMetricValue(value, scatter.metricUnit, false)
          }
          topPickLegendLabel={scatter.topPickLegendLabel ?? 'Top pick'}
          otherCandidatesLegendLabel={
            scatter.otherCandidatesLegendLabel ?? 'Other top options'
          }
          headingVariant='report'
          className='py-0'
        />
      ) : null}
    </section>
  )
}
